import React, { Component } from 'react';
import productsService from '../../services/productsService';
import './ProductManager.css';

class ProductManager extends Component {
    constructor(props) {
        super(props);
        this.state = {
            products: [],
            loading: true,
            error: null,
            searchTerm: '',
            showModal: false,
            showDetailsModal: false,
            showUploadModal: false,
            editingProduct: null,
            selectedProduct: null,
            formData: {
                productName: '',
                displayName: '',
                description: '',
                bucketPersistent: '',
                bucketTemporary: '',
                bucketTransient: '',
                inputFileZip: '',
                rootFileName: '',
                drawingFilePath: '',
                viewableZipFile: '',
                thumbnailUrl: '',
                referenceImageUrl: '',
                quoteTemplateFile: '',
                inventorVersion: 'Autodesk.Inventor+2026'
            },
            modalPosition: { x: 0, y: 0 },
            isDragging: false,
            dragOffset: { x: 0, y: 0 },
            showParamModal: false,
            paramFormData: {
                paramName: '',
                displayName: '',
                paramType: 'text',
                defaultValue: '',
                minValue: '',
                maxValue: '',
                unit: '',
                isVisible: true,
                sortOrder: 0
            },
            editingParam: null,
            availableParameters: [],
            loadingParams: false,
            // Upload wizard state
            uploadStep: 1,
            uploadFile: null,
            uploadDragging: false,
            uploadProgress: 0,
            uploadStatus: '',
            uploadError: '',
            uploading: false,
            analyzing: false,
            analysisResult: null,
            adoptStatus: '',
            adoptedProject: null,
            adoptedStats: null,
            uploadFormData: {
                displayName: '',
                description: '',
                rootFileName: '',
                drawingFilePath: '',
                inventorVersion: 'Autodesk.Inventor+2026'
            }
        };
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleFileDrop = this.handleFileDrop.bind(this);
        this.handleFileDragOver = this.handleFileDragOver.bind(this);
        this.handleFileDragLeave = this.handleFileDragLeave.bind(this);
        this.handleFileSelect = this.handleFileSelect.bind(this);
        this.fileInputRef = React.createRef();
    }

    componentDidMount() {
        this.loadProducts();
        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);
    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
    }

    handleMouseDown(e) {
        var modal = document.getElementById('pm-draggable-modal');
        if (modal) {
            var rect = modal.getBoundingClientRect();
            this.setState({
                isDragging: true,
                dragOffset: {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                }
            });
        }
    }

    handleMouseMove(e) {
        if (this.state.isDragging) {
            this.setState({
                modalPosition: {
                    x: e.clientX - this.state.dragOffset.x,
                    y: e.clientY - this.state.dragOffset.y
                }
            });
        }
    }

    handleMouseUp() {
        this.setState({ isDragging: false });
    }

    async loadProducts() {
        try {
            var products = await productsService.getAll();
            this.setState({ products: products, loading: false });
        } catch (err) {
            this.setState({ error: err.message, loading: false });
        }
    }

    async loadAvailableParameters(productName) {
        if (!productName) return;

        this.setState({ loadingParams: true, availableParameters: [] });

        try {
            var response = await fetch('/parameters/' + encodeURIComponent(productName), {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            });

            if (response.ok) {
                var paramsData = await response.json();
                var params = [];
                for (var key in paramsData) {
                    if (paramsData.hasOwnProperty(key)) {
                        var p = paramsData[key];
                        params.push({
                            name: key,
                            value: p.value || '',
                            unit: p.unit || '',
                            label: p.label || key,
                            type: p.unit === 'Boolean' ? 'boolean' : (p.unit === 'Text' ? 'text' : 'number'),
                            values: p.values || []
                        });
                    }
                }
                this.setState({
                    availableParameters: params,
                    loadingParams: false
                });
            } else {
                this.setState({ loadingParams: false });
            }
        } catch (err) {
            console.log('Could not load parameters from Inventor:', err);
            this.setState({ loadingParams: false });
        }
    }

    // Upload Modal Methods
    openUploadModal() {
        this.setState({
            showUploadModal: true,
            uploadStep: 1,
            uploadFile: null,
            uploadDragging: false,
            uploadProgress: 0,
            uploadStatus: '',
            uploadError: '',
            uploading: false,
            analyzing: false,
            analysisResult: null,
            adoptStatus: '',
            adoptedProject: null,
            adoptedStats: null,
            modalPosition: { x: 0, y: 0 },
            uploadFormData: {
                displayName: '',
                description: '',
                rootFileName: '',
                drawingFilePath: '',
                inventorVersion: 'Autodesk.Inventor+2026'
            }
        });
    }

    closeUploadModal() {
        this.setState({
            showUploadModal: false,
            uploadFile: null,
            uploadStep: 1,
            analysisResult: null,
            uploadError: '',
            adoptStatus: '',
            adoptedProject: null,
            adoptedStats: null
        });
    }

    handleFileDragOver(e) {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ uploadDragging: true });
    }

    handleFileDragLeave(e) {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ uploadDragging: false });
    }

    handleFileDrop(e) {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ uploadDragging: false });

        var files = e.dataTransfer.files;
        if (files && files.length > 0) {
            this.processSelectedFile(files[0]);
        }
    }

    handleFileSelect(e) {
        var files = e.target.files;
        if (files && files.length > 0) {
            this.processSelectedFile(files[0]);
        }
    }

    processSelectedFile(file) {
        // Validate file type
        if (!file.name.toLowerCase().endsWith('.zip')) {
            this.setState({ uploadError: 'Please select a ZIP file containing your Inventor assembly.' });
            return;
        }

        // Validate file size (3GB max)
        var maxSize = 3 * 1024 * 1024 * 1024;
        if (file.size > maxSize) {
            this.setState({ uploadError: 'File size exceeds 3GB limit.' });
            return;
        }

        // Set default display name from file name
        var displayName = file.name.replace(/\.zip$/i, '').replace(/[_-]/g, ' ');

        this.setState({
            uploadFile: file,
            uploadError: '',
            uploadFormData: {
                ...this.state.uploadFormData,
                displayName: displayName
            }
        });

        // Analyze the ZIP file
        this.analyzeZipFile(file);
    }

    async analyzeZipFile(file) {
        this.setState({ analyzing: true, analysisResult: null });

        try {
            var result = await productsService.analyzeZip(file);

            // Auto-select first .iam file as root if available
            var rootFile = '';
            var drawingFile = '';

            if (result.assemblyFiles && result.assemblyFiles.length > 0) {
                rootFile = result.assemblyFiles[0];
            }
            if (result.drawingFiles && result.drawingFiles.length > 0) {
                drawingFile = result.drawingFiles[0];
            }

            this.setState({
                analyzing: false,
                analysisResult: result,
                uploadFormData: {
                    ...this.state.uploadFormData,
                    rootFileName: rootFile,
                    drawingFilePath: drawingFile
                }
            });
        } catch (err) {
            this.setState({
                analyzing: false,
                analysisResult: null,
                uploadError: 'Could not analyze ZIP file: ' + err.message
            });
        }
    }

    handleUploadFormChange(field, value) {
        this.setState({
            uploadFormData: {
                ...this.state.uploadFormData,
                [field]: value
            }
        });
    }

    nextUploadStep() {
        var currentStep = this.state.uploadStep;
        if (currentStep === 1 && this.state.uploadFile) {
            this.setState({ uploadStep: 2 });
        } else if (currentStep === 2) {
            this.setState({ uploadStep: 3 });
            this.performUpload();
        }
    }

    prevUploadStep() {
        var currentStep = this.state.uploadStep;
        if (currentStep > 1) {
            this.setState({ uploadStep: currentStep - 1 });
        }
    }

    async performUpload() {
        var self = this;
        this.setState({
            uploading: true,
            uploadProgress: 0,
            uploadStatus: 'Uploading file to server...',
            uploadError: '',
            adoptStatus: ''
        });

        try {
            // Step 1: Upload ZIP to server and create DB record
            var result = await productsService.uploadProduct(
                this.state.uploadFile,
                this.state.uploadFormData,
                function(progress) {
                    self.setState({
                        uploadProgress: progress,
                        uploadStatus: progress < 100 ? 'Uploading file... ' + progress + '%' : 'File uploaded. Starting APS processing...'
                    });
                }
            );

            var packageId = result.packageId;
            var productId = result.productId;
            var productName = result.productName;

            if (!packageId) {
                throw new Error('No packageId returned from server. Upload may have failed.');
            }

            // Step 2: Trigger APS Design Automation adopt job via SignalR
            self.setState({
                uploadProgress: 100,
                uploading: false,
                adoptStatus: 'processing',
                uploadStatus: 'Processing model with Autodesk Design Automation...'
            });

            productsService.adoptProduct(
                packageId,
                // onStart
                function() {
                    self.setState({
                        uploadStatus: 'Connected to APS. Processing model (this may take 1-3 minutes)...'
                    });
                },
                // onComplete — DA finished, parameters extracted, viewable created
                function(newProject, stats, reportUrl) {
                    self.setState({
                        adoptStatus: 'complete',
                        adoptedProject: newProject,
                        adoptedStats: stats,
                        uploadStatus: 'success',
                        uploadStep: 4
                    });

                    // Auto-import parameters from the adopted project into our products table
                    self.autoImportParameters(productId, productName);

                    // Reload products list
                    self.loadProducts();
                },
                // onError — DA processing failed
                function(errorData) {
                    var errorMessage = 'APS processing failed.';
                    if (errorData && errorData.messages) {
                        errorMessage = errorData.messages.join('. ');
                    } else if (errorData && errorData.title) {
                        errorMessage = errorData.title;
                    } else if (typeof errorData === 'string') {
                        errorMessage = errorData;
                    }
                    self.setState({
                        adoptStatus: 'error',
                        uploadError: errorMessage,
                        uploadStatus: 'error'
                    });
                }
            );

        } catch (err) {
            this.setState({
                uploading: false,
                uploadError: err.message,
                uploadStatus: 'error',
                adoptStatus: 'error'
            });
        }
    }

    async autoImportParameters(productId, productName) {
        // Wait a moment for the server to finish caching
        await new Promise(function(resolve) { setTimeout(resolve, 2000); });

        try {
            // Fetch parameters from the newly adopted project via the APS parameters endpoint
            var response = await fetch('/parameters/' + encodeURIComponent(productName), {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            });

            if (!response.ok) {
                console.log('Could not auto-fetch parameters for ' + productName);
                return;
            }

            var paramsData = await response.json();
            var importCount = 0;

            for (var key in paramsData) {
                if (paramsData.hasOwnProperty(key)) {
                    var p = paramsData[key];
                    var paramType = 'text';
                    if (p.unit === 'Boolean') {
                        paramType = 'boolean';
                    } else if (p.unit !== 'Text' && p.unit !== '') {
                        paramType = 'number';
                    }

                    try {
                        await productsService.addParameter(productId, {
                            paramName: key,
                            displayName: (p.label || key).replace(/_/g, ' '),
                            paramType: paramType,
                            defaultValue: p.value || '',
                            unit: p.unit || '',
                            isVisible: true,
                            sortOrder: importCount
                        });
                        importCount++;
                    } catch (paramErr) {
                        console.log('Could not import parameter ' + key + ':', paramErr);
                    }
                }
            }

            if (importCount > 0) {
                console.log('Auto-imported ' + importCount + ' parameters for ' + productName);
                // Reload products to show updated parameter count
                this.loadProducts();
            }
        } catch (err) {
            console.log('Auto-import parameters failed:', err);
        }
    }

    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        var k = 1024;
        var sizes = ['Bytes', 'KB', 'MB', 'GB'];
        var i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    getFileExtension(filename) {
        return filename.split('.').pop().toLowerCase();
    }

    // Existing modal methods
    openAddModal() {
        this.setState({
            showModal: true,
            editingProduct: null,
            modalPosition: { x: 0, y: 0 },
            availableParameters: [],
            formData: {
                productName: '',
                displayName: '',
                description: '',
                bucketPersistent: '',
                bucketTemporary: '',
                bucketTransient: '',
                inputFileZip: '',
                rootFileName: '',
                drawingFilePath: '',
                viewableZipFile: '',
                thumbnailUrl: '',
                referenceImageUrl: '',
                quoteTemplateFile: '',
                inventorVersion: 'Autodesk.Inventor+2026'
            }
        });
    }

    openEditModal(product) {
        this.setState({
            showModal: true,
            showDetailsModal: false,
            editingProduct: product,
            modalPosition: { x: 0, y: 0 },
            formData: {
                productName: product.productName || '',
                displayName: product.displayName || '',
                description: product.description || '',
                bucketPersistent: product.bucketPersistent || '',
                bucketTemporary: product.bucketTemporary || '',
                bucketTransient: product.bucketTransient || '',
                inputFileZip: product.inputFileZip || '',
                rootFileName: product.rootFileName || '',
                drawingFilePath: product.drawingFilePath || '',
                viewableZipFile: product.viewableZipFile || '',
                thumbnailUrl: product.thumbnailUrl || '',
                referenceImageUrl: product.referenceImageUrl || '',
                quoteTemplateFile: product.quoteTemplateFile || '',
                inventorVersion: product.inventorVersion || 'Autodesk.Inventor+2026'
            }
        });

        this.loadAvailableParameters(product.productName);
    }

    async openDetailsModal(product) {
        try {
            var fullProduct = await productsService.getById(product.id);
            this.setState({
                showDetailsModal: true,
                selectedProduct: fullProduct,
                modalPosition: { x: 0, y: 0 }
            });

            this.loadAvailableParameters(product.productName);
        } catch (err) {
            alert('Failed to load product details: ' + err.message);
        }
    }

    closeModal() {
        this.setState({ showModal: false, editingProduct: null, availableParameters: [] });
    }

    closeDetailsModal() {
        this.setState({ showDetailsModal: false, selectedProduct: null, availableParameters: [] });
    }

    handleChange(field, value) {
        var newFormData = Object.assign({}, this.state.formData);
        newFormData[field] = value;
        this.setState({ formData: newFormData });
    }

    async handleSubmit(e) {
        e.preventDefault();
        var editingProduct = this.state.editingProduct;
        var formData = this.state.formData;

        try {
            if (editingProduct) {
                await productsService.update(editingProduct.id, formData);
            } else {
                await productsService.create(formData);
            }
            this.closeModal();
            this.loadProducts();
        } catch (err) {
            alert(err.message);
        }
    }

    async handleDelete(product) {
        if (!window.confirm('Are you sure you want to delete "' + (product.displayName || product.productName) + '"?')) {
            return;
        }

        try {
            await productsService.delete(product.id);
            this.closeDetailsModal();
            this.loadProducts();
        } catch (err) {
            alert('Failed to delete product: ' + err.message);
        }
    }

    // Parameter methods
    openAddParamModal() {
        this.setState({
            showParamModal: true,
            editingParam: null,
            paramFormData: {
                paramName: '',
                displayName: '',
                paramType: 'text',
                defaultValue: '',
                minValue: '',
                maxValue: '',
                unit: '',
                isVisible: true,
                sortOrder: this.state.selectedProduct.parameters ? this.state.selectedProduct.parameters.length : 0
            }
        });
    }

    openEditParamModal(param) {
        this.setState({
            showParamModal: true,
            editingParam: param,
            paramFormData: {
                paramName: param.paramName || '',
                displayName: param.displayName || '',
                paramType: param.paramType || 'text',
                defaultValue: param.defaultValue || '',
                minValue: param.minValue || '',
                maxValue: param.maxValue || '',
                unit: param.unit || '',
                isVisible: param.isVisible !== false,
                sortOrder: param.sortOrder || 0
            }
        });
    }

    closeParamModal() {
        this.setState({ showParamModal: false, editingParam: null });
    }

    handleParamChange(field, value) {
        var newParamFormData = Object.assign({}, this.state.paramFormData);
        newParamFormData[field] = value;
        this.setState({ paramFormData: newParamFormData });
    }

    selectAvailableParam(param) {
        this.setState({
            paramFormData: {
                paramName: param.name,
                displayName: param.label || param.name.replace(/_/g, ' '),
                paramType: param.type === 'boolean' ? 'boolean' : (param.type === 'text' ? 'text' : 'number'),
                defaultValue: param.value || '',
                minValue: '',
                maxValue: '',
                unit: param.unit || '',
                isVisible: true,
                sortOrder: this.state.selectedProduct.parameters ? this.state.selectedProduct.parameters.length : 0
            }
        });
    }

    async handleParamSubmit(e) {
        e.preventDefault();
        var editingParam = this.state.editingParam;
        var paramFormData = this.state.paramFormData;
        var selectedProduct = this.state.selectedProduct;

        try {
            if (editingParam) {
                await productsService.updateParameter(editingParam.id, paramFormData);
            } else {
                await productsService.addParameter(selectedProduct.id, paramFormData);
            }
            this.closeParamModal();
            var fullProduct = await productsService.getById(selectedProduct.id);
            this.setState({ selectedProduct: fullProduct });
            this.loadProducts();
        } catch (err) {
            alert(err.message);
        }
    }

    async handleDeleteParam(param) {
        if (!window.confirm('Are you sure you want to delete parameter "' + param.paramName + '"?')) {
            return;
        }

        try {
            await productsService.deleteParameter(param.id);
            var fullProduct = await productsService.getById(this.state.selectedProduct.id);
            this.setState({ selectedProduct: fullProduct });
            this.loadProducts();
        } catch (err) {
            alert('Failed to delete parameter: ' + err.message);
        }
    }

    async importAllParameters() {
        var availableParameters = this.state.availableParameters;
        var selectedProduct = this.state.selectedProduct;
        var existingParamNames = selectedProduct.parameters ? selectedProduct.parameters.map(function(p) { return p.paramName; }) : [];

        if (availableParameters.length === 0) {
            alert('No parameters available to import');
            return;
        }

        var newParams = availableParameters.filter(function(p) {
            return existingParamNames.indexOf(p.name) === -1;
        });

        if (newParams.length === 0) {
            alert('All parameters have already been imported');
            return;
        }

        if (!window.confirm('Import ' + newParams.length + ' parameter(s)?')) {
            return;
        }

        try {
            for (var i = 0; i < newParams.length; i++) {
                var param = newParams[i];
                await productsService.addParameter(selectedProduct.id, {
                    paramName: param.name,
                    displayName: param.label || param.name.replace(/_/g, ' '),
                    paramType: param.type === 'boolean' ? 'boolean' : (param.type === 'text' ? 'text' : 'number'),
                    defaultValue: param.value || '',
                    unit: param.unit || '',
                    isVisible: true,
                    sortOrder: i
                });
            }

            var fullProduct = await productsService.getById(selectedProduct.id);
            this.setState({ selectedProduct: fullProduct });
            this.loadProducts();
            alert('Imported ' + newParams.length + ' parameter(s)');
        } catch (err) {
            alert('Failed to import parameters: ' + err.message);
        }
    }

    formatDate(dateString) {
        return new Date(dateString).toLocaleDateString();
    }

    renderUploadWizard() {
        var state = this.state;
        var self = this;

        return (
            <div className="pm-modal-overlay">
                <div id="pm-draggable-modal" className="pm-modal pm-modal-large">
                    <div className="pm-modal-header" onMouseDown={this.handleMouseDown}>
                        <h3>Upload Inventor Model</h3>
                        <button className="pm-modal-close" onClick={function() { self.closeUploadModal(); }}>&times;</button>
                    </div>
                    <div className="pm-modal-body">
                        {/* Wizard Steps */}
                        <div className="pm-wizard-steps">
                            <div className={'pm-wizard-step ' + (state.uploadStep >= 1 ? 'active' : '') + (state.uploadStep > 1 ? ' completed' : '')}>
                                <span className="pm-wizard-step-number">{state.uploadStep > 1 ? '✓' : '1'}</span>
                                <span className="pm-wizard-step-label">Select File</span>
                            </div>
                            <div className="pm-wizard-step-connector"></div>
                            <div className={'pm-wizard-step ' + (state.uploadStep >= 2 ? 'active' : '') + (state.uploadStep > 2 ? ' completed' : '')}>
                                <span className="pm-wizard-step-number">{state.uploadStep > 2 ? '✓' : '2'}</span>
                                <span className="pm-wizard-step-label">Configure</span>
                            </div>
                            <div className="pm-wizard-step-connector"></div>
                            <div className={'pm-wizard-step ' + (state.uploadStep >= 3 ? 'active' : '') + (state.uploadStep > 3 ? ' completed' : '')}>
                                <span className="pm-wizard-step-number">{state.uploadStep > 3 ? '✓' : '3'}</span>
                                <span className="pm-wizard-step-label">Upload</span>
                            </div>
                        </div>

                        <div className="pm-wizard-content">
                            {/* Step 1: Select File */}
                            {state.uploadStep === 1 && (
                                <div>
                                    <div
                                        className={'pm-upload-zone ' + (state.uploadDragging ? 'dragging' : '') + (state.uploadFile ? ' has-file' : '')}
                                        onDragOver={this.handleFileDragOver}
                                        onDragLeave={this.handleFileDragLeave}
                                        onDrop={this.handleFileDrop}
                                        onClick={function() { self.fileInputRef.current.click(); }}
                                    >
                                        <div className="pm-upload-icon">
                                            {state.uploadFile ? '📦' : '📁'}
                                        </div>
                                        {state.uploadFile ? (
                                            <div>
                                                <h4>File Selected</h4>
                                                <p className="pm-file-name">{state.uploadFile.name}</p>
                                                <p className="pm-file-size">{self.formatFileSize(state.uploadFile.size)}</p>
                                            </div>
                                        ) : (
                                            <div>
                                                <h4>Drag & Drop your ZIP file here</h4>
                                                <p>or <span className="pm-upload-browse">browse</span> to select</p>
                                            </div>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        ref={this.fileInputRef}
                                        className="pm-file-input"
                                        accept=".zip"
                                        onChange={this.handleFileSelect}
                                    />

                                    {state.uploadError && (
                                        <p className="pm-progress-status error">{state.uploadError}</p>
                                    )}

                                    {state.analyzing && (
                                        <div className="pm-analyzing">
                                            <span className="pm-spinner"></span>
                                            <p>Analyzing ZIP contents...</p>
                                        </div>
                                    )}

                                    {state.analysisResult && (
                                        <div className="pm-file-analysis">
                                            <h5>Files Detected:</h5>
                                            <div className="pm-file-list">
                                                {state.analysisResult.assemblyFiles && state.analysisResult.assemblyFiles.map(function(file, index) {
                                                    return (
                                                        <div key={'asm-' + index} className="pm-file-item">
                                                            <span className="pm-file-item-icon iam">IAM</span>
                                                            <span className="pm-file-item-name">{file}</span>
                                                        </div>
                                                    );
                                                })}
                                                {state.analysisResult.drawingFiles && state.analysisResult.drawingFiles.map(function(file, index) {
                                                    return (
                                                        <div key={'dwg-' + index} className="pm-file-item">
                                                            <span className="pm-file-item-icon idw">IDW</span>
                                                            <span className="pm-file-item-name">{file}</span>
                                                        </div>
                                                    );
                                                })}
                                                {state.analysisResult.partFiles && state.analysisResult.partFiles.length > 0 && (
                                                    <div className="pm-file-item">
                                                        <span className="pm-file-item-icon ipt">IPT</span>
                                                        <span className="pm-file-item-name">{state.analysisResult.partFiles.length} part file(s)</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="pm-max-size-note">
                                        <strong>Note:</strong> Maximum file size is 3GB. ZIP should contain your Inventor assembly (.iam), parts (.ipt), and optionally drawings (.idw/.dwg).
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Configure */}
                            {state.uploadStep === 2 && (
                                <div>
                                    <div className="pm-form-section">
                                        <h4>Product Information</h4>
                                        <div className="pm-form-group">
                                            <label>Display Name *</label>
                                            <input
                                                type="text"
                                                value={state.uploadFormData.displayName}
                                                onChange={function(e) { self.handleUploadFormChange('displayName', e.target.value); }}
                                                placeholder="e.g., Sliding Door Assembly"
                                            />
                                            <span className="pm-field-hint">This name will be shown to users</span>
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Description</label>
                                            <textarea
                                                value={state.uploadFormData.description}
                                                onChange={function(e) { self.handleUploadFormChange('description', e.target.value); }}
                                                placeholder="Brief description of this product..."
                                                rows="2"
                                            />
                                        </div>
                                    </div>

                                    <div className="pm-form-section">
                                        <h4>Assembly Configuration</h4>
                                        <div className="pm-file-select-group">
                                            <label>Root Assembly File *</label>
                                            <select
                                                value={state.uploadFormData.rootFileName}
                                                onChange={function(e) { self.handleUploadFormChange('rootFileName', e.target.value); }}
                                            >
                                                <option value="">-- Select assembly file --</option>
                                                {state.analysisResult && state.analysisResult.assemblyFiles && state.analysisResult.assemblyFiles.map(function(file, index) {
                                                    return <option key={index} value={file}>{file}</option>;
                                                })}
                                            </select>
                                            <span className="pm-file-select-hint">The main .iam assembly file</span>
                                        </div>
                                        <div className="pm-file-select-group">
                                            <label>Drawing File (Optional)</label>
                                            <select
                                                value={state.uploadFormData.drawingFilePath}
                                                onChange={function(e) { self.handleUploadFormChange('drawingFilePath', e.target.value); }}
                                            >
                                                <option value="">-- No drawing --</option>
                                                {state.analysisResult && state.analysisResult.drawingFiles && state.analysisResult.drawingFiles.map(function(file, index) {
                                                    return <option key={index} value={file}>{file}</option>;
                                                })}
                                            </select>
                                            <span className="pm-file-select-hint">The .idw or .dwg drawing file for PDF generation</span>
                                        </div>
                                    </div>

                                    <div className="pm-form-section">
                                        <h4>Inventor Version</h4>
                                        <div className="pm-form-group">
                                            <select
                                                value={state.uploadFormData.inventorVersion}
                                                onChange={function(e) { self.handleUploadFormChange('inventorVersion', e.target.value); }}
                                            >
                                                <option value="Autodesk.Inventor+2026">Autodesk Inventor 2026</option>
                                                <option value="Autodesk.Inventor+2025">Autodesk Inventor 2025</option>
                                                <option value="Autodesk.Inventor+2024">Autodesk Inventor 2024</option>
                                                <option value="Autodesk.Inventor+2023">Autodesk Inventor 2023</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Uploading + APS Processing */}
                            {state.uploadStep === 3 && !state.uploadError && (
                                <div>
                                    <div className="pm-upload-progress">
                                        {/* File upload progress */}
                                        <div className="pm-progress-section">
                                            <p className="pm-progress-label">
                                                {state.uploadProgress >= 100 ? '✓' : '⬆'} File Upload
                                            </p>
                                            <div className="pm-progress-bar-container">
                                                <div className="pm-progress-bar" style={{ width: state.uploadProgress + '%' }}>
                                                    <span>{state.uploadProgress}%</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* APS Processing progress */}
                                        {state.adoptStatus && (
                                            <div className="pm-progress-section">
                                                <p className="pm-progress-label">
                                                    {state.adoptStatus === 'complete' ? '✓' : '⚙'} APS Design Automation
                                                </p>
                                                {state.adoptStatus === 'processing' && (
                                                    <div className="pm-processing-indicator">
                                                        <div className="pm-processing-bar">
                                                            <div className="pm-processing-bar-animated"></div>
                                                        </div>
                                                        <p className="pm-processing-text">
                                                            Processing model — extracting parameters, creating 3D viewable, generating BOM...
                                                        </p>
                                                        <p className="pm-processing-note">
                                                            This typically takes 1-3 minutes depending on model complexity.
                                                        </p>
                                                    </div>
                                                )}
                                                {state.adoptStatus === 'complete' && (
                                                    <p className="pm-progress-label" style={{ color: '#28a745' }}>Processing complete!</p>
                                                )}
                                            </div>
                                        )}

                                        <p className="pm-progress-status">{state.uploadStatus}</p>
                                    </div>
                                    <div className="pm-configure-preview">
                                        <div className="pm-configure-preview-image">Preview</div>
                                        <div className="pm-configure-preview-info">
                                            <h4>{state.uploadFormData.displayName}</h4>
                                            <p><strong>File:</strong> {state.uploadFile ? state.uploadFile.name : ''}</p>
                                            <p><strong>Root:</strong> {state.uploadFormData.rootFileName}</p>
                                            {state.uploadFormData.drawingFilePath && (
                                                <p><strong>Drawing:</strong> {state.uploadFormData.drawingFilePath}</p>
                                            )}
                                            <p><strong>Version:</strong> {state.uploadFormData.inventorVersion}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 3 with Error */}
                            {state.uploadStep === 3 && state.uploadError && (
                                <div>
                                    <p className="pm-progress-status error">{state.uploadError}</p>
                                    <p>Please go back and try again, or check your file and settings.</p>
                                    <p className="pm-processing-note">
                                        Common issues: model saved in newer Inventor version than selected, missing parts in ZIP, 
                                        or assembly references files outside the ZIP package.
                                    </p>
                                </div>
                            )}

                            {/* Step 4: Success */}
                            {state.uploadStep === 4 && (
                                <div className="pm-upload-success">
                                    <div className="pm-upload-success-icon">✓</div>
                                    <h4>Upload & Processing Complete!</h4>
                                    <p>Your product "{state.uploadFormData.displayName}" has been uploaded and processed by Autodesk Design Automation.</p>
                                    <p>Parameters have been automatically extracted and the 3D viewable has been created.</p>
                                    {state.adoptedStats && (
                                        <p className="pm-processing-note">
                                            Processing credits used: {state.adoptedStats.credits || 'N/A'}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="pm-modal-footer">
                        {state.uploadStep === 1 && (
                            <div className="pm-wizard-nav" style={{ width: '100%' }}>
                                <button className="pm-btn-cancel" onClick={function() { self.closeUploadModal(); }}>Cancel</button>
                                <button
                                    className="pm-btn-next"
                                    disabled={!state.uploadFile || state.analyzing}
                                    onClick={function() { self.nextUploadStep(); }}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                        {state.uploadStep === 2 && (
                            <div className="pm-wizard-nav" style={{ width: '100%' }}>
                                <button className="pm-btn-back" onClick={function() { self.prevUploadStep(); }}>Back</button>
                                <button
                                    className="pm-btn-upload"
                                    disabled={!state.uploadFormData.displayName || !state.uploadFormData.rootFileName}
                                    onClick={function() { self.nextUploadStep(); }}
                                >
                                    Upload Product
                                </button>
                            </div>
                        )}
                        {state.uploadStep === 3 && (
                            <div className="pm-wizard-nav" style={{ width: '100%' }}>
                                {state.uploadError ? (
                                    <button className="pm-btn-back" onClick={function() { self.prevUploadStep(); }}>Back</button>
                                ) : (
                                    <span></span>
                                )}
                                <button className="pm-btn-cancel" disabled={state.uploading || state.adoptStatus === 'processing'} onClick={function() { self.closeUploadModal(); }}>
                                    {(state.uploading || state.adoptStatus === 'processing') ? 'Processing...' : 'Cancel'}
                                </button>
                            </div>
                        )}
                        {state.uploadStep === 4 && (
                            <div className="pm-wizard-nav" style={{ width: '100%' }}>
                                <span></span>
                                <button className="pm-btn-save" onClick={function() { self.closeUploadModal(); }}>Done</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    renderProductRow(product) {
        var self = this;
        var description = product.description || '';
        var companyName = product.companyName || '';
        var thumbnailUrl = product.thumbnailUrl || null;

        return (
            <div
                key={product.id}
                className="pm-row"
                onClick={function() { self.openDetailsModal(product); }}
            >
                <div className="pm-row-thumbnail">
                    {thumbnailUrl ? (
                        <img
                            src={thumbnailUrl}
                            alt={product.displayName || product.productName}
                            onError={function(e) {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div
                        className="pm-row-no-thumbnail"
                        style={{ display: thumbnailUrl ? 'none' : 'flex' }}
                    >
                        📦
                    </div>
                </div>
                <div className="pm-row-info">
                    <h3 className="pm-row-name">{product.displayName || product.productName}</h3>
                    <p className="pm-row-description">
                        {description || 'No description'}
                    </p>
                    <div className="pm-row-details">
                        {companyName && <span className="pm-row-tag company">{companyName}</span>}
                        <span className="pm-row-tag">{product.parameters ? product.parameters.length : 0} parameter(s)</span>
                        <span className={'pm-row-tag ' + (product.isActive ? 'active' : 'inactive')}>
                            {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                </div>
                <div className="pm-row-action">
                    <button className="pm-row-button">
                        Manage →
                    </button>
                </div>
            </div>
        );
    }

    render() {
        var state = this.state;
        var self = this;

        if (state.loading) {
            return <div className="product-manager"><p>Loading...</p></div>;
        }

        if (state.error) {
            return <div className="product-manager"><p className="pm-error">Error: {state.error}</p></div>;
        }

        var modalStyle = {};
        if (state.modalPosition.x !== 0 || state.modalPosition.y !== 0) {
            modalStyle = {
                position: 'fixed',
                left: state.modalPosition.x + 'px',
                top: state.modalPosition.y + 'px',
                margin: 0
            };
        }

        // Filter products by search term
        var products = state.products;
        var searchTerm = state.searchTerm || '';
        if (searchTerm.trim()) {
            var term = searchTerm.toLowerCase();
            products = products.filter(function(product) {
                var name = (product.displayName || product.productName || '').toLowerCase();
                var desc = (product.description || '').toLowerCase();
                var company = (product.companyName || '').toLowerCase();
                return name.indexOf(term) !== -1 || desc.indexOf(term) !== -1 || company.indexOf(term) !== -1;
            });
        }

        return (
            <div className="product-manager">
                <div className="pm-header">
                    <div className="pm-header-left">
                        <h2>Product Manager</h2>
                        <span className="pm-count">{products.length} product{products.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="pm-header-right">
                        <div className="pm-search">
                            <input
                                type="text"
                                placeholder="Filter products..."
                                value={searchTerm}
                                onChange={function(e) { self.setState({ searchTerm: e.target.value }); }}
                            />
                        </div>
                        <button onClick={function() { self.openUploadModal(); }} className="pm-btn-add">
                            ↑ Upload Model
                        </button>
                        <button onClick={function() { self.openAddModal(); }} className="pm-btn-add pm-btn-secondary">
                            + Add Manually
                        </button>
                    </div>
                </div>

                {products.length === 0 ? (
                    <div className="pm-empty">
                        <div className="pm-empty-icon">📦</div>
                        <h3>No Products Available</h3>
                        <p>
                            {searchTerm
                                ? 'No products match your search. Try a different term.'
                                : 'No products configured yet. Click "Upload Model" to add your first Inventor assembly.'
                            }
                        </p>
                    </div>
                ) : (
                    <div className="pm-list">
                        {products.map(function(product) { return self.renderProductRow(product); })}
                    </div>
                )}

                {/* Upload Wizard Modal */}
                {state.showUploadModal && this.renderUploadWizard()}

                {/* Product Details Modal */}
                {state.showDetailsModal && state.selectedProduct && (
                    <div className="pm-modal-overlay">
                        <div
                            id="pm-draggable-modal"
                            className="pm-modal pm-modal-large"
                            style={modalStyle}
                        >
                            <div
                                className="pm-modal-header"
                                onMouseDown={this.handleMouseDown}
                            >
                                <h3>{state.selectedProduct.displayName || state.selectedProduct.productName}</h3>
                                <button className="pm-modal-close" onClick={function() { self.closeDetailsModal(); }}>&times;</button>
                            </div>
                            <div className="pm-modal-body">
                                <div className="pm-details-section">
                                    <h4>MODEL DETAILS</h4>
                                    <div className="pm-detail-row">
                                        <span className="pm-detail-label">PRODUCT NAME</span>
                                        <span className="pm-detail-value">{state.selectedProduct.productName}</span>
                                    </div>
                                    {state.selectedProduct.description && (
                                        <div className="pm-detail-row">
                                            <span className="pm-detail-label">DESCRIPTION</span>
                                            <span className="pm-detail-value">{state.selectedProduct.description}</span>
                                        </div>
                                    )}
                                    <div className="pm-detail-row">
                                        <span className="pm-detail-label">STORAGE BUCKET</span>
                                        <span className="pm-detail-value">{state.selectedProduct.bucketPersistent || '-'}</span>
                                    </div>
                                    <div className="pm-detail-row">
                                        <span className="pm-detail-label">WORKING BUCKET</span>
                                        <span className="pm-detail-value">{state.selectedProduct.bucketTemporary || '-'}</span>
                                    </div>
                                    <div className="pm-detail-row">
                                        <span className="pm-detail-label">CACHE BUCKET</span>
                                        <span className="pm-detail-value">{state.selectedProduct.bucketTransient || '-'}</span>
                                    </div>
                                    <div className="pm-detail-row">
                                        <span className="pm-detail-label">VERSION</span>
                                        <span className="pm-detail-value">{state.selectedProduct.inventorVersion}</span>
                                    </div>
                                </div>

                                <div className="pm-details-section">
                                    <h4>ZIP FILE DETAILS</h4>
                                    <div className="pm-detail-row">
                                        <span className="pm-detail-label">INPUT FILE ZIP</span>
                                        <span className="pm-detail-value">{state.selectedProduct.inputFileZip || '-'}</span>
                                    </div>
                                    <div className="pm-detail-row">
                                        <span className="pm-detail-label">ROOT FILE NAME</span>
                                        <span className="pm-detail-value">{state.selectedProduct.rootFileName || '-'}</span>
                                    </div>
                                    <div className="pm-detail-row">
                                        <span className="pm-detail-label">DRAWING FILE PATH</span>
                                        <span className="pm-detail-value">{state.selectedProduct.drawingFilePath || '-'}</span>
                                    </div>
                                </div>

                                <div className="pm-details-section">
                                    <div className="pm-section-header">
                                        <h4>PARAMETERS</h4>
                                        <div className="pm-param-buttons">
                                            {state.availableParameters.length > 0 && (
                                                <button className="pm-btn-import" onClick={function() { self.importAllParameters(); }}>
                                                    Import All ({state.availableParameters.length})
                                                </button>
                                            )}
                                            <button className="pm-btn-add-param" onClick={function() { self.openAddParamModal(); }}>
                                                + Add Parameter
                                            </button>
                                        </div>
                                    </div>

                                    {state.loadingParams && (
                                        <p className="pm-loading-params">Loading parameters from Inventor...</p>
                                    )}

                                    {state.availableParameters.length > 0 && (
                                        <div className="pm-available-params">
                                            <p className="pm-available-label">Available from Inventor model ({state.availableParameters.length}):</p>
                                            <div className="pm-param-chips">
                                                {state.availableParameters.map(function(param, index) {
                                                    var isAdded = state.selectedProduct.parameters &&
                                                        state.selectedProduct.parameters.some(function(p) { return p.paramName === param.name; });
                                                    return (
                                                        <span
                                                            key={index}
                                                            className={'pm-param-chip ' + (isAdded ? 'added' : '')}
                                                            onClick={function() { if (!isAdded) { self.selectAvailableParam(param); self.openAddParamModal(); } }}
                                                            title={isAdded ? 'Already added' : 'Click to add'}
                                                        >
                                                            {param.label || param.name}
                                                            {param.value && <small> = {param.value}</small>}
                                                            {isAdded && <span className="pm-chip-check">✓</span>}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {state.selectedProduct.parameters && state.selectedProduct.parameters.length > 0 ? (
                                        <table className="pm-params-table">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Display Name</th>
                                                    <th>Type</th>
                                                    <th>Default</th>
                                                    <th>Unit</th>
                                                    <th>Visible</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {state.selectedProduct.parameters.map(function(param) {
                                                    return (
                                                        <tr key={param.id}>
                                                            <td>{param.paramName}</td>
                                                            <td>{param.displayName || '-'}</td>
                                                            <td>{param.paramType}</td>
                                                            <td>{param.defaultValue || '-'}</td>
                                                            <td>{param.unit || '-'}</td>
                                                            <td>{param.isVisible ? 'Yes' : 'No'}</td>
                                                            <td>
                                                                <button
                                                                    className="pm-btn-edit-sm"
                                                                    onClick={function() { self.openEditParamModal(param); }}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    className="pm-btn-delete-sm"
                                                                    onClick={function() { self.handleDeleteParam(param); }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p className="pm-no-params">No parameters defined yet.</p>
                                    )}
                                </div>
                            </div>
                            <div className="pm-modal-footer">
                                <button
                                    className="pm-btn-edit"
                                    onClick={function() { self.openEditModal(state.selectedProduct); }}
                                >
                                    Edit Product
                                </button>
                                <button
                                    className="pm-btn-delete"
                                    onClick={function() { self.handleDelete(state.selectedProduct); }}
                                >
                                    Delete Product
                                </button>
                                <button className="pm-btn-close" onClick={function() { self.closeDetailsModal(); }}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add/Edit Product Modal */}
                {state.showModal && (
                    <div className="pm-modal-overlay">
                        <div
                            id="pm-draggable-modal"
                            className="pm-modal pm-modal-large"
                            style={modalStyle}
                        >
                            <div
                                className="pm-modal-header"
                                onMouseDown={this.handleMouseDown}
                            >
                                <h3>{state.editingProduct ? 'Edit Product' : 'Add Product'}</h3>
                                <button className="pm-modal-close" onClick={function() { self.closeModal(); }}>&times;</button>
                            </div>
                            <form onSubmit={function(e) { self.handleSubmit(e); }}>
                                <div className="pm-modal-body">
                                    <div className="pm-form-section">
                                        <h4>Basic Information</h4>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group">
                                                <label>Product Name *</label>
                                                <input
                                                    type="text"
                                                    value={state.formData.productName}
                                                    onChange={function(e) { self.handleChange('productName', e.target.value); }}
                                                    placeholder="e.g., MY_PRODUCT_001"
                                                    required
                                                />
                                            </div>
                                            <div className="pm-form-group">
                                                <label>Display Name</label>
                                                <input
                                                    type="text"
                                                    value={state.formData.displayName}
                                                    onChange={function(e) { self.handleChange('displayName', e.target.value); }}
                                                    placeholder="e.g., My Product"
                                                />
                                            </div>
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Description</label>
                                            <textarea
                                                value={state.formData.description}
                                                onChange={function(e) { self.handleChange('description', e.target.value); }}
                                                placeholder="Product description..."
                                                rows="2"
                                            />
                                        </div>
                                    </div>

                                    <div className="pm-form-section">
                                        <h4>Storage Configuration</h4>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group">
                                                <label>Storage Bucket (Persistent)</label>
                                                <input
                                                    type="text"
                                                    value={state.formData.bucketPersistent}
                                                    onChange={function(e) { self.handleChange('bucketPersistent', e.target.value); }}
                                                    placeholder="e.g., myapp-storage"
                                                />
                                                <small className="pm-field-hint">Long-term storage for source files</small>
                                            </div>
                                            <div className="pm-form-group">
                                                <label>Working Bucket (Temporary)</label>
                                                <input
                                                    type="text"
                                                    value={state.formData.bucketTemporary}
                                                    onChange={function(e) { self.handleChange('bucketTemporary', e.target.value); }}
                                                    placeholder="e.g., myapp-working"
                                                />
                                                <small className="pm-field-hint">Short-term storage (24 hours)</small>
                                            </div>
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Cache Bucket (Transient)</label>
                                            <input
                                                type="text"
                                                value={state.formData.bucketTransient}
                                                onChange={function(e) { self.handleChange('bucketTransient', e.target.value); }}
                                                placeholder="e.g., myapp-cache"
                                            />
                                            <small className="pm-field-hint">Very short-term cache (2 hours)</small>
                                        </div>
                                    </div>

                                    <div className="pm-form-section">
                                        <h4>File Configuration</h4>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group">
                                                <label>Input File ZIP</label>
                                                <input
                                                    type="text"
                                                    value={state.formData.inputFileZip}
                                                    onChange={function(e) { self.handleChange('inputFileZip', e.target.value); }}
                                                    placeholder="e.g., MyProduct.zip"
                                                />
                                            </div>
                                            <div className="pm-form-group">
                                                <label>Viewable ZIP File</label>
                                                <input
                                                    type="text"
                                                    value={state.formData.viewableZipFile}
                                                    onChange={function(e) { self.handleChange('viewableZipFile', e.target.value); }}
                                                    placeholder="e.g., MyProductViewable.zip"
                                                />
                                            </div>
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Root File Name</label>
                                            <input
                                                type="text"
                                                value={state.formData.rootFileName}
                                                onChange={function(e) { self.handleChange('rootFileName', e.target.value); }}
                                                placeholder="e.g., inputfile/ASSEMBLIES/MyProduct.iam"
                                            />
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Drawing File Path</label>
                                            <input
                                                type="text"
                                                value={state.formData.drawingFilePath}
                                                onChange={function(e) { self.handleChange('drawingFilePath', e.target.value); }}
                                                placeholder="e.g., inputfile/ASSEMBLIES/MyProduct.idw"
                                            />
                                        </div>
                                    </div>

                                    <div className="pm-form-section">
                                        <h4>Images & Version</h4>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group">
                                                <label>Thumbnail URL</label>
                                                <input
                                                    type="text"
                                                    value={state.formData.thumbnailUrl}
                                                    onChange={function(e) { self.handleChange('thumbnailUrl', e.target.value); }}
                                                    placeholder="https://..."
                                                />
                                            </div>
                                            <div className="pm-form-group">
                                                <label>Reference Image URL</label>
                                                <input
                                                    type="text"
                                                    value={state.formData.referenceImageUrl}
                                                    onChange={function(e) { self.handleChange('referenceImageUrl', e.target.value); }}
                                                    placeholder="https://..."
                                                />
                                            </div>
                                        </div>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group">
                                                <label>Quote Template File</label>
                                                <input
                                                    type="text"
                                                    value={state.formData.quoteTemplateFile}
                                                    onChange={function(e) { self.handleChange('quoteTemplateFile', e.target.value); }}
                                                    placeholder="e.g., QuoteTemplate.docx"
                                                />
                                            </div>
                                            <div className="pm-form-group">
                                                <label>Inventor Version</label>
                                                <select
                                                    value={state.formData.inventorVersion}
                                                    onChange={function(e) { self.handleChange('inventorVersion', e.target.value); }}
                                                >
                                                    <option value="Autodesk.Inventor+2026">Autodesk.Inventor+2026</option>
                                                    <option value="Autodesk.Inventor+2025">Autodesk.Inventor+2025</option>
                                                    <option value="Autodesk.Inventor+2024">Autodesk.Inventor+2024</option>
                                                    <option value="Autodesk.Inventor+2023">Autodesk.Inventor+2023</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="pm-modal-footer">
                                    <button type="button" className="pm-btn-cancel" onClick={function() { self.closeModal(); }}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="pm-btn-save">
                                        {state.editingProduct ? 'Save Changes' : 'Create Product'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Add/Edit Parameter Modal */}
                {state.showParamModal && (
                    <div className="pm-modal-overlay">
                        <div className="pm-modal pm-modal-small">
                            <div className="pm-modal-header">
                                <h3>{state.editingParam ? 'Edit Parameter' : 'Add Parameter'}</h3>
                                <button className="pm-modal-close" onClick={function() { self.closeParamModal(); }}>&times;</button>
                            </div>
                            <form onSubmit={function(e) { self.handleParamSubmit(e); }}>
                                <div className="pm-modal-body">
                                    {state.availableParameters.length > 0 && !state.editingParam && (
                                        <div className="pm-form-group">
                                            <label>Select from Inventor Model</label>
                                            <select
                                                onChange={function(e) {
                                                    var param = state.availableParameters.find(function(p) { return p.name === e.target.value; });
                                                    if (param) self.selectAvailableParam(param);
                                                }}
                                                value=""
                                            >
                                                <option value="">-- Choose a parameter --</option>
                                                {state.availableParameters.map(function(param, index) {
                                                    return (
                                                        <option key={index} value={param.name}>
                                                            {param.label || param.name} {param.value ? '(' + param.value + ')' : ''}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    )}
                                    <div className="pm-form-group">
                                        <label>Parameter Name *</label>
                                        <input
                                            type="text"
                                            value={state.paramFormData.paramName}
                                            onChange={function(e) { self.handleParamChange('paramName', e.target.value); }}
                                            placeholder="e.g., FRAME_WIDTH"
                                            required
                                        />
                                    </div>
                                    <div className="pm-form-group">
                                        <label>Display Name</label>
                                        <input
                                            type="text"
                                            value={state.paramFormData.displayName}
                                            onChange={function(e) { self.handleParamChange('displayName', e.target.value); }}
                                            placeholder="e.g., Frame Width"
                                        />
                                    </div>
                                    <div className="pm-form-row">
                                        <div className="pm-form-group">
                                            <label>Type</label>
                                            <select
                                                value={state.paramFormData.paramType}
                                                onChange={function(e) { self.handleParamChange('paramType', e.target.value); }}
                                            >
                                                <option value="text">Text</option>
                                                <option value="number">Number</option>
                                                <option value="boolean">Boolean</option>
                                                <option value="select">Select</option>
                                            </select>
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Unit</label>
                                            <input
                                                type="text"
                                                value={state.paramFormData.unit}
                                                onChange={function(e) { self.handleParamChange('unit', e.target.value); }}
                                                placeholder="e.g., mm, in"
                                            />
                                        </div>
                                    </div>
                                    <div className="pm-form-row">
                                        <div className="pm-form-group">
                                            <label>Default Value</label>
                                            <input
                                                type="text"
                                                value={state.paramFormData.defaultValue}
                                                onChange={function(e) { self.handleParamChange('defaultValue', e.target.value); }}
                                            />
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Sort Order</label>
                                            <input
                                                type="number"
                                                value={state.paramFormData.sortOrder}
                                                onChange={function(e) { self.handleParamChange('sortOrder', parseInt(e.target.value) || 0); }}
                                            />
                                        </div>
                                    </div>
                                    <div className="pm-form-row">
                                        <div className="pm-form-group">
                                            <label>Min Value</label>
                                            <input
                                                type="text"
                                                value={state.paramFormData.minValue}
                                                onChange={function(e) { self.handleParamChange('minValue', e.target.value); }}
                                            />
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Max Value</label>
                                            <input
                                                type="text"
                                                value={state.paramFormData.maxValue}
                                                onChange={function(e) { self.handleParamChange('maxValue', e.target.value); }}
                                            />
                                        </div>
                                    </div>
                                    <div className="pm-form-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={state.paramFormData.isVisible}
                                                onChange={function(e) { self.handleParamChange('isVisible', e.target.checked); }}
                                            />
                                            {' '}Visible to users
                                        </label>
                                    </div>
                                </div>
                                <div className="pm-modal-footer">
                                    <button type="button" className="pm-btn-cancel" onClick={function() { self.closeParamModal(); }}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="pm-btn-save">
                                        {state.editingParam ? 'Save Changes' : 'Add Parameter'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default ProductManager;